import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { endpoints } from "../lib/api";
import { getAll, put, queueSync, STORES } from "../lib/db";
import { ACCOUNT_TYPES, ACCOUNT_TYPE_META, typeMeta } from "../lib/accountTypes";
import { STAGE_LABELS, daysAgo } from "../lib/crmHealth";
import { HealthChip } from "../components/ContactHealth";
import AccountPicker, { DuplicateWarning, OwnerSelect } from "../components/AccountPicker";
import { CardScanButton } from "../components/CardScanner";
import {
    Users, Plus, X, PencilSimple, MagnifyingGlass, ArrowElbowDownRight, CaretUp, CaretDown,
    CaretRight, Phone, EnvelopeSimple, FunnelSimple, TreeStructure, ArrowCounterClockwise,
} from "@phosphor-icons/react";

const STAGES = ["lead", "contacted", "proposal", "negotiation", "won", "active", "closed_lost"];
const STAGE_ORDER = Object.fromEntries(STAGES.map((s, i) => [s, i]));
const STAGE_COLOR = {
    lead: "#64748b", contacted: "#0891b2", proposal: "var(--rom-orange)", negotiation: "#d97706",
    won: "#00853F", active: "#2563eb", closed_lost: "#94a3b8",
};
const CONTACT_FILTERS = [
    ["all", "Any contact status"], ["needs", "Needs contact"], ["due_soon", "Due soon"], ["ok", "On track"], ["off", "Not tracked"],
];
const PREFS_KEY = "accounts_list_prefs_v1";
const DEFAULT_PREFS = { type: "all", stage: "all", owner: "all", contact: "all", nested: true, sort: { key: "name", dir: "asc" } };

function loadPrefs() {
    try { return { ...DEFAULT_PREFS, ...(JSON.parse(localStorage.getItem(PREFS_KEY) || "null") || {}) }; }
    catch { return DEFAULT_PREFS; }
}

export default function Clients() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [health, setHealth] = useState({});
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(empty("customer"));
    const [q, setQ] = useState("");
    const [prefs, setPrefs] = useState(loadPrefs);
    const [collapsed, setCollapsed] = useState(new Set());

    useEffect(() => { reload(); }, []);
    useEffect(() => { try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* private mode */ } }, [prefs]);

    async function reload() {
        setLoading(true);
        try {
            const { data } = await endpoints.clients();
            setItems(data || []);
        } catch {
            setItems(await getAll(STORES.clients));
        }
        try {
            const { data } = await endpoints.crmOverview();
            setHealth(Object.fromEntries((data?.accounts || []).map((a) => [a.id, a])));
        } catch { /* offline — contact column stays empty */ }
        setLoading(false);
    }

    async function save() {
        const isNew = !draft.id;
        const base = isNew ? { ...draft, owner_id: draft.owner_id || user?.id || "", owner: draft.owner || user?.name || "" } : draft;
        const obj = { ...base, id: draft.id || crypto.randomUUID(), updated_at: new Date().toISOString() };
        await put(STORES.clients, obj);
        try { await endpoints.saveClient(obj); }
        catch { await queueSync("clients", obj); }
        setOpen(false);
        setDraft(empty("customer"));
        await reload();
    }

    const set = (patch) => setPrefs((p) => ({ ...p, ...patch }));
    const byId = useMemo(() => Object.fromEntries(items.map((c) => [c.id, c])), [items]);
    const childrenOf = useMemo(() => {
        const m = {};
        items.forEach((c) => { if (c.parent_id && byId[c.parent_id]) (m[c.parent_id] = m[c.parent_id] || []).push(c); });
        return m;
    }, [items, byId]);

    const owners = useMemo(() => {
        const m = new Map();
        items.forEach((c) => { if (c.owner_id) m.set(c.owner_id, c.owner || "Unknown"); });
        return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [items]);

    const typeCounts = useMemo(() => {
        const m = { all: items.length };
        ACCOUNT_TYPES.forEach((t) => { m[t] = items.filter((c) => (c.type || "customer") === t).length; });
        return m;
    }, [items]);

    const primaryOf = (c) => c.contacts?.find((x) => x.is_primary) || c.contacts?.[0];

    const matches = (c) => {
        if (prefs.type !== "all" && (c.type || "customer") !== prefs.type) return false;
        if (prefs.stage !== "all" && (c.stage || "lead") !== prefs.stage) return false;
        if (prefs.owner === "mine" && c.owner_id !== user?.id) return false;
        if (prefs.owner === "none" && c.owner_id) return false;
        if (!["all", "mine", "none"].includes(prefs.owner) && c.owner_id !== prefs.owner) return false;
        if (prefs.contact !== "all") {
            const s = health[c.id]?.status;
            if (prefs.contact === "needs" ? !(s === "overdue" || s === "never") : s !== prefs.contact) return false;
        }
        if (q) {
            const p = primaryOf(c);
            const hay = `${c.name} ${p?.name || c.contact_name || ""} ${p?.email || c.email || ""} ${p?.phone || c.phone || ""} ${c.address || ""} ${(c.contacts || []).map((x) => x.name).join(" ")} ${byId[c.parent_id]?.name || ""}`.toLowerCase();
            if (!hay.includes(q.toLowerCase())) return false;
        }
        return true;
    };

    const sortVal = (c) => {
        const k = prefs.sort.key;
        if (k === "name") return (c.name || "").toLowerCase();
        if (k === "type") return typeMeta(c.type).label;
        if (k === "stage") return STAGE_ORDER[c.stage || "lead"] ?? 99;
        if (k === "owner") return (c.owner || "~").toLowerCase();
        if (k === "contact") {
            // Urgency: overdue / never-contacted (most days overdue) > due soon > on track (longest ago first).
            // desc = most urgent first; asc = most recently contacted first.
            const h = health[c.id];
            const days = h?.days_since_contact ?? 0;
            if (h?.status === "overdue" || h?.status === "never") return 1e6 + (h.days_overdue || 0);
            if (h?.status === "due_soon") return 5e5 + days;
            return days;
        }
        return 0;
    };
    const untracked = (c) => { const h = health[c.id]; return !h || h.status === "off"; };
    const cmp = (a, b) => {
        // Accounts without a contact cadence always sink to the bottom of a last-contact sort.
        if (prefs.sort.key === "contact" && untracked(a) !== untracked(b)) return untracked(a) ? 1 : -1;
        const av = sortVal(a), bv = sortVal(b);
        const r = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
        return (prefs.sort.dir === "asc" ? r : -r) || (a.name || "").localeCompare(b.name || "");
    };

    // Rows: nested = parents with their matching sites underneath (a parent is
    // shown dimmed when only its sites match); flat = every matching account.
    const rows = useMemo(() => {
        if (!prefs.nested) return items.filter(matches).sort(cmp).map((c) => ({ c, depth: 0 }));
        const tops = items.filter((c) => !(c.parent_id && byId[c.parent_id]));
        const out = [];
        tops.sort(cmp).forEach((t) => {
            const kids = (childrenOf[t.id] || []).filter(matches).sort(cmp);
            const self = matches(t);
            if (!self && !kids.length) return;
            out.push({ c: t, depth: 0, dim: !self, kids: (childrenOf[t.id] || []).length, shownKids: kids.length });
            if (!collapsed.has(t.id)) kids.forEach((k) => out.push({ c: k, depth: 1 }));
        });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, health, prefs, q, collapsed, byId, childrenOf, user?.id]);

    const shown = rows.filter((r) => !r.dim).length;
    const filtersOn = prefs.stage !== "all" || prefs.owner !== "all" || prefs.contact !== "all" || q;

    function sortBy(key) {
        set({ sort: prefs.sort.key === key ? { key, dir: prefs.sort.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "contact" ? "desc" : "asc" } });
    }
    function toggle(id) {
        setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }
    function openNew() {
        setDraft(empty(prefs.type !== "all" ? prefs.type : "customer"));
        setOpen(true);
    }
    function openEdit(c) { setDraft({ ...empty(c.type || "customer"), ...c }); setOpen(true); }

    return (
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
            <header className="flex flex-wrap items-end justify-between gap-4 mb-5">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">CRM</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <Users size={28} weight="bold" className="text-[var(--rom-orange)]" />
                        Accounts
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">Clients with their property managers and sites, plus brokers, insurers and adjusting firms.</p>
                </div>
                <div className="flex items-center gap-2">
                    <CardScanButton onDone={reload} />
                    <button className="btn btn-primary" onClick={openNew} data-testid="client-new">
                        <Plus size={14} weight="bold" /> New account
                    </button>
                </div>
            </header>

            {/* Type tabs */}
            <div className="flex gap-1 overflow-x-auto pb-1 mb-3 -mx-1 px-1" role="tablist" data-testid="accounts-type-tabs">
                {[["all", "All accounts", Users], ...ACCOUNT_TYPES.map((t) => [t, ACCOUNT_TYPE_META[t].plural, ACCOUNT_TYPE_META[t].icon])].map(([t, label, Icon]) => (
                    <button key={t} role="tab" aria-selected={prefs.type === t} onClick={() => set({ type: t })} data-testid={`accounts-tab-${t}`}
                        className={`shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-[6px] text-sm font-semibold border transition-colors ${
                            prefs.type === t ? "bg-[var(--rom-navy)] text-white border-[var(--rom-navy)]" : "bg-white text-[var(--rom-text-2)] border-[var(--rom-border-strong)] hover:border-[var(--rom-navy)]"}`}>
                        <Icon size={14} weight="bold" /> {label}
                        <span className={`text-[11px] font-bold px-1.5 rounded-full ${prefs.type === t ? "bg-white/20" : "bg-slate-100"}`}>{typeCounts[t] ?? 0}</span>
                    </button>
                ))}
            </div>

            {/* Search + filters */}
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="accounts-filters">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)]" />
                    <input className="input pl-9" placeholder="Search name, contact, email, phone, address…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search" />
                </div>
                <Select value={prefs.stage} onChange={(v) => set({ stage: v })} testId="accounts-filter-stage">
                    <option value="all">All stages</option>
                    {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                </Select>
                <Select value={prefs.owner} onChange={(v) => set({ owner: v })} testId="accounts-filter-owner">
                    <option value="all">All owners</option>
                    <option value="mine">My accounts</option>
                    {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                    <option value="none">Unassigned</option>
                </Select>
                <Select value={prefs.contact} onChange={(v) => set({ contact: v })} testId="accounts-filter-contact">
                    {CONTACT_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
                <button onClick={() => set({ nested: !prefs.nested })} data-testid="accounts-toggle-nested" title="Show property managers / sites under their parent company"
                    className={`h-9 px-3 rounded-[4px] border text-sm font-semibold flex items-center gap-1.5 ${prefs.nested ? "bg-slate-100 border-[var(--rom-navy)] text-[var(--rom-navy)]" : "bg-white border-[var(--rom-border-strong)] text-[var(--rom-text-2)]"}`}>
                    <TreeStructure size={14} weight="bold" /> {prefs.nested ? "Grouped by parent" : "Flat list"}
                </button>
                {filtersOn && (
                    <button onClick={() => { setQ(""); set({ stage: "all", owner: "all", contact: "all" }); }} className="h-9 px-2 text-sm font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-navy)] flex items-center gap-1" data-testid="accounts-clear-filters">
                        <ArrowCounterClockwise size={13} /> Clear
                    </button>
                )}
                <div className="ml-auto text-xs text-[var(--rom-text-3)] flex items-center gap-1" data-testid="accounts-count">
                    <FunnelSimple size={12} /> {shown} of {typeCounts.all} accounts
                </div>
            </div>

            <div className="rom-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="rom-table min-w-[860px]" data-testid="accounts-table">
                        <thead>
                            <tr>
                                <Th k="name" sort={prefs.sort} onSort={sortBy}>Account</Th>
                                <Th k="type" sort={prefs.sort} onSort={sortBy}>Type</Th>
                                <th>Primary contact</th>
                                <Th k="stage" sort={prefs.sort} onSort={sortBy}>Stage</Th>
                                <Th k="owner" sort={prefs.sort} onSort={sortBy}>Owner</Th>
                                <Th k="contact" sort={prefs.sort} onSort={sortBy}>Last contact</Th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !items.length && <tr><td colSpan={7} className="text-center text-[var(--rom-text-3)] py-10">Loading…</td></tr>}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-[var(--rom-text-3)] py-10">
                                    No accounts match.{filtersOn && <> <button className="text-[var(--rom-navy)] font-semibold hover:underline" onClick={() => { setQ(""); set({ stage: "all", owner: "all", contact: "all" }); }}>Clear filters</button></>}
                                </td></tr>
                            )}
                            {rows.map(({ c, depth, dim, kids, shownKids }) => (
                                <Row key={c.id} c={c} depth={depth} dim={dim} kids={kids} shownKids={shownKids}
                                    parentName={!prefs.nested || depth === 0 ? byId[c.parent_id]?.name : null}
                                    collapsed={collapsed.has(c.id)} onToggle={() => toggle(c.id)}
                                    health={health[c.id]} primary={primaryOf(c)}
                                    onOpen={() => navigate(`/crm/accounts/${c.id}`)} onEdit={() => openEdit(c)} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="client-modal">
                    <div className="rom-card w-full max-w-xl max-h-[92vh] overflow-y-auto">
                        <div className="rom-card-head">
                            <h3>{draft.id ? "Edit account" : "New account"}</h3>
                            <button onClick={() => setOpen(false)}><X size={18} /></button>
                        </div>
                        <div className="rom-card-body grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="label">Type</label>
                                <div className="flex gap-2 flex-wrap">
                                    {ACCOUNT_TYPES.map((t) => (
                                        <button key={t}
                                            className={`px-3 py-2 text-xs font-semibold rounded border ${draft.type === t ? "bg-[var(--rom-navy)] text-white border-[var(--rom-navy)]" : "bg-white border-[var(--rom-border-strong)]"}`}
                                            onClick={() => setDraft({ ...draft, type: t })}
                                            data-testid={`client-type-${t}`}>
                                            {ACCOUNT_TYPE_META[t].label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <FieldX label="Name" v={draft.name} on={(v) => setDraft({ ...draft, name: v })} testId="client-name" className="col-span-2" />
                            <FieldX label="Contact" v={draft.contact_name} on={(v) => setDraft({ ...draft, contact_name: v })} />
                            <FieldX label="Email" v={draft.email} on={(v) => setDraft({ ...draft, email: v })} />
                            <FieldX label="Phone" v={draft.phone} on={(v) => setDraft({ ...draft, phone: v })} />
                            <FieldX label="Website" v={draft.website} on={(v) => setDraft({ ...draft, website: v })} />
                            <FieldX label="Address" v={draft.address} on={(v) => setDraft({ ...draft, address: v })} className="col-span-2" />
                            <div>
                                <label className="label">Parent company</label>
                                <AccountPicker value={draft.parent_id} excludeId={draft.id} onChange={(id) => setDraft((d) => ({ ...d, parent_id: id || null }))} placeholder="None — top level" testId="client-parent-picker" />
                            </div>
                            <div>
                                <label className="label">Account owner</label>
                                <OwnerSelect ownerId={draft.owner_id || (!draft.id ? user?.id : "")} ownerName={draft.owner || (!draft.id ? user?.name : "")}
                                    onChange={(o) => setDraft((d) => ({ ...d, ...o }))} testId="client-owner-select" />
                            </div>
                            {draft.type === "insurer" && (
                                <>
                                    <FieldX label="Policy #" v={draft.policy_number} on={(v) => setDraft({ ...draft, policy_number: v })} />
                                    <FieldX label="Claim #" v={draft.claim_number} on={(v) => setDraft({ ...draft, claim_number: v })} />
                                </>
                            )}
                            <div className="col-span-2">
                                <DuplicateWarning name={draft.name} email={draft.email} phone={draft.phone} website={draft.website} excludeId={draft.id}
                                    onUseAsParent={(h) => setDraft((d) => ({ ...d, parent_id: h.id }))} />
                            </div>
                            <div className="col-span-2 flex justify-end gap-2">
                                <button className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={save} disabled={!draft.name} data-testid="client-save">Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Th({ k, sort, onSort, children }) {
    const on = sort.key === k;
    return (
        <th aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
            <button onClick={() => onSort(k)} className={`flex items-center gap-1 uppercase ${on ? "text-[var(--rom-text)]" : ""}`} data-testid={`accounts-sort-${k}`}>
                {children}
                {on ? (sort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />) : <CaretDown size={10} className="opacity-25" />}
            </button>
        </th>
    );
}

function Row({ c, depth, dim, kids, shownKids, parentName, collapsed, onToggle, health, primary, onOpen, onEdit }) {
    const meta = typeMeta(c.type);
    const Icon = meta.icon;
    const stage = c.stage || "lead";
    const phone = primary?.phone || c.phone;
    const email = primary?.email || c.email;
    return (
        <tr className={`cursor-pointer group ${dim ? "opacity-55" : ""} ${depth ? "bg-slate-50/50" : ""}`} onClick={onOpen} data-testid={`client-${c.id}`}>
            <td>
                <div className="flex items-center gap-1.5 min-w-0" style={{ paddingLeft: depth * 22 }}>
                    {depth === 0 && kids > 0 ? (
                        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-200 shrink-0" title={collapsed ? "Show sites" : "Hide sites"} data-testid={`accounts-expand-${c.id}`}>
                            {collapsed ? <CaretRight size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
                        </button>
                    ) : depth ? <ArrowElbowDownRight size={12} className="text-[var(--rom-text-3)] shrink-0" /> : <span className="w-5 shrink-0" />}
                    <div className="min-w-0">
                        <div className="font-semibold truncate">{c.name}</div>
                        {parentName && <div className="text-[11px] text-[var(--rom-text-3)] truncate">part of {parentName}</div>}
                        {depth === 0 && kids > 0 && (
                            <div className="text-[11px] text-[var(--rom-text-3)]">{kids} site{kids === 1 ? "" : "s"}{shownKids < kids ? ` · ${shownKids} shown` : ""}</div>
                        )}
                    </div>
                </div>
            </td>
            <td><span className="flex items-center gap-1.5 text-xs text-[var(--rom-text-2)] whitespace-nowrap"><Icon size={13} weight="bold" /> {meta.label}</span></td>
            <td>
                <div className="text-sm truncate max-w-[220px]">{primary?.name || c.contact_name || <span className="text-[var(--rom-text-3)]">—</span>}</div>
                <div className="text-[11px] text-[var(--rom-text-3)] flex items-center gap-2 mt-0.5">
                    {phone && <a href={`tel:${phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 hover:text-[var(--rom-navy)] whitespace-nowrap"><Phone size={10} /> {phone}</a>}
                    {email && <a href={`mailto:${email}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 hover:text-[var(--rom-navy)] truncate max-w-[180px]"><EnvelopeSimple size={10} /> {email}</a>}
                </div>
            </td>
            <td><span className="text-xs font-bold whitespace-nowrap" style={{ color: STAGE_COLOR[stage] }}>{STAGE_LABELS[stage] || stage}</span></td>
            <td className="text-sm text-[var(--rom-text-2)] whitespace-nowrap">{c.owner || <span className="text-[var(--rom-text-3)]">Unassigned</span>}</td>
            <td>
                {health ? (
                    <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="text-xs text-[var(--rom-text-2)] w-[68px]">{health.last_contact_at ? daysAgo(health.days_since_contact) : "Never"}</span>
                        <HealthChip health={health} />
                    </div>
                ) : <span className="text-xs text-[var(--rom-text-3)]">—</span>}
            </td>
            <td>
                <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-1.5 text-[var(--rom-text-3)] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-[var(--rom-navy)]" title="Quick edit" data-testid={`client-edit-${c.id}`}>
                    <PencilSimple size={14} weight="bold" />
                </button>
            </td>
        </tr>
    );
}

function Select({ value, onChange, children, testId }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className={`h-9 px-3 rounded-[4px] border text-sm font-semibold bg-white ${value !== "all" ? "border-[var(--rom-navy)] text-[var(--rom-navy)]" : "border-[var(--rom-border-strong)]"}`}>
            {children}
        </select>
    );
}

function FieldX({ label, v, on, className = "", testId }) {
    return (
        <div className={className}>
            <label className="label">{label}</label>
            <input className="input" value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testId} />
        </div>
    );
}

function empty(type) {
    return { id: "", type, name: "", contact_name: "", email: "", phone: "", website: "", address: "",
        policy_number: "", claim_number: "", parent_id: null, owner: "", owner_id: "" };
}
